import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppstoreOutlined, PieChartOutlined, UserOutlined } from '@ant-design/icons';

import { useAppSelector } from 'app/hooks';
import { selectCurrentUser } from 'features/auth/authSlice';
import { UserProviderId, UserRole } from 'models/IUser';
import { NavItemType } from 'types/menu';

export const useMenuItems = () => {
  const { t } = useTranslation();
  const authUser = useAppSelector(selectCurrentUser);

  return useMemo<{ items: NavItemType[] }>(() => {
    const administration: NavItemType = {
      id: 'group-administration',
      icon: PieChartOutlined,
      type: 'group',
      children: [
        {
          id: 'collapse-administration',
          title: t('administration', 'Administration'),
          type: 'collapse',
          icon: PieChartOutlined,
          children: [
            {
              id: 'item-devices',
              title: t('devices', 'Devices & connections'),
              type: 'item',
              url: '/admin/devices'
            },
            {
              id: 'item-users',
              title: t('users', 'Users'),
              type: 'item',
              url: '/admin/users'
            },
            {
              id: 'item-organizations',
              title: t('organizations', 'Organizations'),
              type: 'item',
              url: '/admin/organizations'
            },
            ...(authUser?.role === UserRole.Admin
              ? [
                  {
                    id: 'item-literals',
                    title: t('literals', 'Literals'),
                    type: 'item',
                    url: '/admin/literals'
                  } as NavItemType
                ]
              : [])
          ]
        }
      ]
    };

    const patients: NavItemType = {
      id: 'group-patients',
      icon: UserOutlined,
      type: 'group',
      children: [
        {
          id: 'item-patients',
          title: authUser?.provider !== UserProviderId.SMILE ? t('patients', 'Patients') : t('participants', 'Participants'),
          type: 'item',
          icon: UserOutlined,
          url: '/patients'
        },
        ...(authUser?.provider === UserProviderId.EXCARE && (authUser?.role === UserRole.Admin || authUser?.role === UserRole.Manager)
          ? [
              {
                id: 'item-data-coverage',
                title: t('data_coverage', 'Data Coverage'),
                type: 'item',
                icon: AppstoreOutlined,
                url: '/coverage'
              } as NavItemType
            ]
          : [])
      ]
    };

    return { items: authUser?.role === UserRole.Clinician ? [patients] : [administration, patients] };
  }, [authUser, t]);
};